import { FC, useMemo } from "react";

import { useGlobalNotificationsContext } from "@/context/notifications-context";

import { DefaultPill } from "shared/ui/pills/default-pill";
import { clsxMerge } from "shared/lib/helpers";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface NotificationLabelPropsI extends PropsWithClassNameI {
  id?: string;
  isPulsating?: boolean;
}

type NotificationTypeI = ValueOfObjectFields<typeof NOTIFICATION_MAPPED_IDS>;

export const NOTIFICATION_MAPPED_IDS = {
  NOTIFICATIONS: "notifications",
  MESSAGES: "messages",
  COACHING: "coaching",
  TOTAL_MESSAGES: "total_messages",
  EMAILS: "emails",
  PERFORMANCE: "performance",
} as const;

export const NotificationLabel: FC<NotificationLabelPropsI> = ({
  id,
  className,
  isPulsating,
}) => {
  const typedId = id as NotificationTypeI;

  const { notificationCounters } = useGlobalNotificationsContext();
  const { unread, message_count, coaching_count, unread_email } =
    notificationCounters || {};

  const notificationsMap = useMemo(
    () => ({
      [NOTIFICATION_MAPPED_IDS.NOTIFICATIONS]:
        unread && unread > 0 ? unread : null,
      [NOTIFICATION_MAPPED_IDS.MESSAGES]:
        message_count && message_count > 0 ? message_count : null,
      [NOTIFICATION_MAPPED_IDS.COACHING]:
        coaching_count && coaching_count > 0 ? coaching_count : null,
      [NOTIFICATION_MAPPED_IDS.TOTAL_MESSAGES]:
        (message_count || 0) + (unread_email || 0),
      [NOTIFICATION_MAPPED_IDS.EMAILS]:
        unread_email && unread_email > 0 ? unread_email : null,
      [NOTIFICATION_MAPPED_IDS.PERFORMANCE]:
        coaching_count && coaching_count > 0 ? coaching_count : null,
    }),
    [notificationCounters]
  );

  const notificationOfType = notificationsMap[typedId];

  if (!notificationOfType) {
    return null;
  }

  return (
    <DefaultPill
      className={clsxMerge(
        className,
        isPulsating && "animate-pulse-alt",
        (notificationOfType || 0) >= 10 ? "px-1" : "px-2"
      )}
    >
      {notificationOfType}
    </DefaultPill>
  );
};
